import React, { ReactNode } from "react";
import styled from "styled-components";

import {
    CompactColor,
    ExpressionReactContext,
    ExpressionRenderType,
    expressionSettings,
} from "./Structure/ExpressionReactContext";
import { ExpressionBuilder, XExpressionEditorData } from "./ExpressionBuilder";
import { ExpressionEditLayer } from "./ExpressionEditLayer";
import { ExpressionValueType } from "../rule-engine-core/Expressions/ExpressionValueType";
import { DataEditControl } from "../CommonUI/DataEditing/DataEditControl";
import { Box } from "@mui/material";
import { EditGrayIcon } from "../CommonUI/Icon/EditGrayIcon";
import { UIHover } from "../CommonUI/BuildlingBlocks/UIHover";
import VariablePicker from "../CommonUI/VariablePicker/VariablePicker";
import { ExpressionConfiguration } from "../rule-engine-core/Expressions/ExpressionConfiguration";

export enum ExpressionViewEmptyBehavior {
    EXPRESSION = "expression",
    VALUE = "value",
    VARIABLE = "variable",
}

interface IExpressionView extends XExpressionEditorData {
    color?: CompactColor;

    label?: string;
    emptyBehavior?: ExpressionViewEmptyBehavior;

    hideValidation?: boolean;

    groupBracket?: {};
    onUpdate?: () => void;
    onDone?: () => void;
    open?: boolean;
    defaultOpen?: boolean;

    onOpen?: () => void;

    showClear?: boolean;
    clearName?: string;

    renderValueEditor?: (expression: ExpressionConfiguration) => ReactNode;
}

export class ExpressionView extends React.Component<IExpressionView, { open: boolean }> {
    static contextType = ExpressionReactContext;
    context!: React.ContextType<typeof ExpressionReactContext>;

    private ref = React.createRef<HTMLDivElement>();

    constructor(props: IExpressionView) {
        super(props);

        this.state = {
            open: !!this.props.open || !!props.defaultOpen,
        };
    }

    componentDidUpdate(prevProps: IExpressionView) {
        if (this.props.open !== undefined && prevProps.open !== this.props.open) {
            this.setState({
                open: this.props.open,
            });
        }
    }

    onOpen = () => {
        if (this.props.onOpen) {
            this.props.onOpen();
        }

        if (this.props.open === undefined || !this.state.open) {
            this.setState({
                open: true,
            });
        }
    };

    onClose = () => {
        this.setState({
            open: false,
        });

        if (this.props.onDone) {
            this.props.onDone();
        }
    };

    render() {
        if (
            this.props.emptyBehavior === ExpressionViewEmptyBehavior.VALUE &&
            (this.props.expression.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET ||
                this.props.expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VALUE)
        ) {
            return (
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    width={"100%"}
                    justifyContent={"space-between"}
                >
                    {this.props.renderValueEditor &&
                        this.props.renderValueEditor(this.props.expression) !== undefined &&
                        this.props.renderValueEditor(this.props.expression)}
                    {(!this.props.renderValueEditor ||
                        this.props.renderValueEditor(this.props.expression) === undefined) && (
                        <DataEditControl
                            forData={this.props.expression.getRuleResult()}
                            mode={"display_mode_edit"}
                            value={this.props.expression.rootExpression.expressionRoot.value}
                            onUpdate={(newVal) => {
                                this.props.expression.rootExpression.expressionRoot.value = newVal;
                            }}
                        />
                    )}
                    <Box
                        paddingRight={"5px"}
                        onClick={() => {
                            this.onOpen();
                        }}
                    >
                        <EditGrayIcon></EditGrayIcon>
                    </Box>
                    {this.state.open && (
                        <ExpressionEditLayer
                            open={this.state.open}
                            expression={this.props.expression}
                            onClose={this.onClose}
                            data-testid={`expression_edit_layer_${this.props.expression._id}`}
                            showClear={this.props.showClear}
                            clearName={this.props.clearName}
                        />
                    )}
                </Box>
            );
        } else if (
            this.props.emptyBehavior === ExpressionViewEmptyBehavior.VARIABLE &&
            (this.props.expression.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET ||
                this.props.expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VARIABLE)
        ) {
            return (
                <>
                    <UIHover
                        renderControl={(_hover) => {
                            return (
                                <>
                                    <Box
                                        padding={"7px"}
                                        display={"flex"}
                                        flexDirection={"row"}
                                        alignItems={"center"}
                                        justifyContent={"space-between"}
                                    >
                                        <>
                                            <VariablePicker
                                                selected={
                                                    this.props.expression.rootExpression.expressionRoot.variableName
                                                }
                                                data={this.props.expression.getInputDefinition()}
                                                isList={this.props.expression.getRuleResult().list}
                                                requiredType={this.props.expression.getRuleResult().type}
                                                onVariableSet={(path: string) => {
                                                    if (path) {
                                                        this.props.expression.rootExpression.expressionRoot.variableName =
                                                            path;
                                                    }
                                                }}
                                                label={this.props.label}
                                                variant={"outlined"}
                                                size={"small"}
                                                fullWidth={true}
                                                hideExpectedType={true}
                                            />
                                            <Box
                                                paddingLeft={"5px"}
                                                paddingRight={"5px"}
                                                onClick={() => {
                                                    this.onOpen();
                                                }}
                                            >
                                                <EditGrayIcon></EditGrayIcon>
                                            </Box>
                                        </>
                                    </Box>
                                </>
                            );
                        }}
                    />

                    {this.state.open && (
                        <ExpressionEditLayer
                            open={this.state.open}
                            expression={this.props.expression}
                            onClose={this.onClose}
                            data-testid={`expression_edit_layer_${this.props.expression._id}`}
                            showClear={this.props.showClear}
                            clearName={this.props.clearName}
                        />
                    )}
                </>
            );
        } else {
            return (
                <>
                    <ExpressionViewDiv
                        data-testid={`expression_compact_${this.props.expression._id}`}
                        className="ExpressionView-root"
                        onClick={() => {
                            this.onOpen();
                        }}
                        ref={this.ref}
                    >
                        <ExpressionCompact isText={this.context?.renderType === ExpressionRenderType.TEXT}>
                            <ExpressionCompactContent isText={this.context?.renderType === ExpressionRenderType.TEXT}>
                                <ExpressionReactContext.Provider
                                    value={
                                        this.context?.renderType === ExpressionRenderType.TEXT
                                            ? this.context
                                            : {
                                                  ...expressionSettings[ExpressionRenderType.COMPACT],
                                                  color: this.props.color,
                                                  hideValidation: this.props.hideValidation,
                                              }
                                    }
                                >
                                    <ExpressionBuilder {...this.props} />
                                </ExpressionReactContext.Provider>
                            </ExpressionCompactContent>
                        </ExpressionCompact>
                    </ExpressionViewDiv>
                    {this.state.open && (
                        <ExpressionEditLayer
                            open={this.state.open}
                            expression={this.props.expression}
                            onClose={this.onClose}
                            data-testid={`expression_edit_layer_${this.props.expression._id}`}
                            showClear={this.props.showClear}
                            clearName={this.props.clearName}
                        />
                    )}
                </>
            );
        }
    }
}

const ExpressionViewDiv = styled.div`
    position: relative;
    padding-bottom: 7px;
    height: 100%;

    .edit-button {
        opacity: 0;
    }

    &:hover {
        .edit-button {
            opacity: 1;
        }
    }
`;

const ExpressionCompact = styled.div<{ isText: boolean }>`
    padding: 10px 16px 10px;
    position: relative;
    overflow: hidden;
    max-width: fit-content;
`;

const ExpressionCompactContent = styled.div<{ isText: boolean }>`
    max-height: ${(props) => (props.isText ? 120 : 60)}px;
    overflow: hidden;

    &::after {
        content: "...";
        position: absolute;
        top: ${(props) => (props.isText ? 120 : 60)}px;
        left: 16px;
    }

    button {
        display: none;
    }
`;

// const StyledButton = styled.div`
//     text-decoration: underline;
//     color: #3f51b5;
//
//     cursor: pointer;
// `;
